import { h } from 'preact';

import React, { useEffect, useRef, useContext } from 'react';

import domPurify from 'dompurify';

import { Sanitize } from '../Sanitize/Sanitize';
import { TextSettings } from './TextSettings';
import { languageUsed, makeText } from '../../util';
import { useDev } from '../../hooks';
import { IsPMContext } from '../../contexts';

import TextOttWrapper from './TextOttWrapper';

import './Text.css';

export const Text = ({
  component,
  language,
  className,
  dev,
  path,
  makeChoice,
  vendorCount = { all: null, iab: null}
}) => {
  const isPM = useContext(IsPMContext);
  const { devActions, devClass, pathString } = useDev(path, dev);
  const nv = useRef(null);

  useEffect(() => {
    if (nv.current) {
      const aTags = nv.current.getElementsByTagName('A');
      for (let i = 0; i < aTags.length; i++) {
        aTags[i].addEventListener('click', handleLinkClick)
        aTags[i].addEventListener('keypress', handleLinkClick)
      }
    };

    return () => {
      if (nv.current) {
        const aTags = nv.current.getElementsByTagName('A');
        for (let i = 0; i < aTags.length; i++) {
          aTags[i].removeEventListener('click', handleLinkClick)
          aTags[i].removeEventListener('keypress', handleLinkClick)
        }
      };
    };
  }, [vendorCount]);


  useEffect(() => {
    const htmlTag = document.getElementsByTagName('html')[0];
    htmlTag.lang = language && language.toLowerCase() || 'EN';
  }, [])
  ''

  function handleLinkClick(e) {
    if (e.key !== undefined && e.key !== 'Enter' && e.key !== ' ') {
      return
    }

    let optionId;

    if (this.dataset.choice) {
      // prefer the new style "data-choice" attribute
      optionId = this.dataset.choice
    } else if (this.href && this.href.indexOf('$$$$:') >= 0) {
      // fallback to old style "href='$$$$:" attribute
      optionId = this.href.split('$$$$:')[1];
    }

    if (optionId) {
      e.preventDefault();

      const choiceOptions = component.settings.choice_options || [];
      const option = choiceOptions.find(o => o.data.button_text === optionId);
      if (option && isPM) {
        makeChoice(option.type, languageUsed(language, settings, 'text'), false, option.data);
      } else if (option && option.data.button_text) {
        makeChoice(option.data.button_text, languageUsed(language, settings, 'text'), false, option.data);
      };
    };
  };

  const settings = new TextSettings(component.settings);
  const styles = {
    ...settings.padding,
    ...settings.margin,
    ...settings.border,
    ...settings.font,
  };

  styles.textAlign = settings.align;
  /* WIDTH & HEIGHT */
  const { paddingLeft, paddingRight } = settings.padding;
  const { marginLeft, marginRight } = settings.margin;
  styles.width = settings.width.type === 'auto' ? 'auto' : settings.width.value + settings.width.type;
  styles.height = settings.height.type === 'auto' ? 'auto' : settings.height.value + settings.height.type;
  if (settings.width.type === '%') {
    const adjustment = paddingLeft + paddingRight + marginLeft + marginRight;
    styles.width = `calc(${settings.width.value}% - ${isNaN(adjustment) ? 0 : adjustment}px`;
  };

  const getChoiceOptionClass = (optionId) => {
    const options = component.settings.choice_options || [];
    const choiceOption = options.find(o => o.data.button_text === optionId);
    const type = choiceOption ? choiceOption.type || choiceOption.action_type : null;
    return type ? `sp_choice_type_${type}` : ''
  }

  /* MANAGE LINK STYLING */
  const linkClassName = `text-link-${path.join('-')}`;
  const linkStyleTagContents = domPurify.sanitize(`
    .${linkClassName}:hover { color:${settings.hover.color} !important; text-decoration:${settings.hover.textDecoration} !important; font-weight: ${settings.hover.fontWeight} !important;  }
  `);
  const linkStyle = `color:${settings.link.color};text-decoration:${settings.link.textDecoration};font-weight: ${settings.link.fontWeight};`;
  let textHTML = makeText(dev, settings, 'text', language)
    .replace(/<a ([^>]*(href)[^>]*)>(.+?(?=<\/a))/g, function (match, attributes, hrefMatch, contents) {
      //removes legacy aria label on older messages
      attributes = attributes.replace('aria-label="message-link"', '')
      const linkSplit = attributes.split('$$$$:')[1];
      const linkId = linkSplit ? linkSplit.split('"')[0] : null;
      const linkTypeClass = getChoiceOptionClass(linkId);
      return `<a class='${linkClassName + ' ' + linkTypeClass} focusable' style='${linkStyle}' ${attributes}>${contents}`;
    })

  if (vendorCount.all !== null || vendorCount.iab !== null) {
    textHTML = textHTML
      .replace(/\$\$!VC-ALL!\$\$/g, vendorCount.all)
      .replace(/\$\$!VC-IAB!\$\$/g, vendorCount.iab)
  }

  const HeadingElement = ['h1', 'h2', 'h3', 'p'].includes(settings.heading) ? settings.heading : 'p'
  const removePTag = ['h1', 'h2', 'h3', 'p'].includes(HeadingElement);

  useEffect(() => {
    if (document.head) {
      document.head.insertAdjacentHTML('beforeend', `<style>${linkStyleTagContents}</style>`)
    }
  }, [])

  return (
    <TextOttWrapper pRef={nv} settings={settings}
    >
      <HeadingElement
        className={'message-component text no-children' + className + devClass}
        style={styles}
        path={dev ? pathString : null}
        ref={element => {
          nv.current = element;
          if (element) {
            if (settings.font.size) {
              element.style.setProperty('font-size', Settings.font.size + 'px', 'important');
            }
          }
        }}
        {...devActions}
        dangerouslySetInnerHTML={{ __html: Sanitize({ html: removePTag ? textHTML.replace(/^\<p\>/, "").replace(/\<\/p\>$/, "") : textHTML }) }}
      />
    </TextOttWrapper >
  );
}
